// Food Web: rabbits, foxes, and (later) grass in a meadow that runs on honest agent rules,
// then a draw-the-arrows web. Lesson, then do it: three steps per round, free play any time.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { createMeadow, step, reset, rab, fox, hidden, WH, BUSHES, BR, FMAX, GRASS_N, grassPct, type Meadow } from './sim';

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

interface Choice { prompt: string; options: string[]; answer: number; why: string }

// ---- the true web for tier 3: eater -> foods ----
const SPECIES = ['grass', 'rabbit', 'mouse', 'fox', 'hawk', 'owl'] as const;
type Sp = typeof SPECIES[number];
const EATS: Record<Sp, Sp[]> = { grass: [], rabbit: ['grass'], mouse: ['grass'], fox: ['rabbit', 'mouse'], hawk: ['rabbit', 'mouse'], owl: ['mouse'] };
const NODE_POS: Record<Sp, [number, number]> = { grass: [0.5, 0.88], rabbit: [0.2, 0.58], mouse: [0.8, 0.58], fox: [0.15, 0.22], hawk: [0.5, 0.12], owl: [0.85, 0.22] };
const NODE_COLOR: Record<Sp, string> = { grass: '#5fbf6a', rabbit: '#f6f3ec', mouse: '#9a9aa3', fox: '#f08a3c', hawk: '#8a5a3b', owl: '#9b6be6' };

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  let attempts = 0, correct = 0, missed = false;
  const t0 = performance.now();
  let tStep = performance.now();
  let si = 0;

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  host.append(lesson, task);

  function setLesson(text: string, t: string) { (lesson.firstElementChild as HTMLElement).textContent = text; task.textContent = `Step ${si + 1} of 3: ${t}`; ctx.speak(t); missed = false; tStep = performance.now(); }

  function report(ok: boolean) {
    if (ok) { if (!missed) { attempts++; correct++; } ctx.report({ skill: 'biology', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `web:${tier}:${si}` }); ctx.sound('ding'); }
    else if (!missed) { attempts++; missed = true; ctx.report({ skill: 'biology', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `web:${tier}:${si}` }); ctx.sound('buzz'); }
    else ctx.sound('buzz');
  }

  function finish() {
    if (tier === 1) ctx.charm('charm.cycle');
    task.textContent = 'Round complete. Keep playing with the meadow if you like.';
    setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1400);
  }

  if (tier === 3) return mountDiagram();
  return mountMeadow();

  // ---------------- meadow (tiers 1 and 2) ----------------
  function mountMeadow() {
    const m: Meadow = createMeadow(ctx.rng, tier === 2);
    let speed = 6;
    const counts = h('div.grid3', null,
      h('div.card.center', { style: { padding: '6px' } }, h('div.small.muted', null, 'Rabbits'), h('div.big', { style: { fontSize: '24px', color: '#4f8fe6' } }, '0')),
      h('div.card.center', { style: { padding: '6px' } }, h('div.small.muted', null, 'Foxes'), h('div.big', { style: { fontSize: '24px', color: '#f08a3c' } }, '0')),
      h('div.card.center', { style: { padding: '6px', visibility: tier === 2 ? 'visible' : 'hidden' } }, h('div.small.muted', null, 'Grass'), h('div.big', { style: { fontSize: '24px', color: '#5fbf6a' } }, '0%')),
    );
    const stage = h('div');
    const graphHost = h('div');
    const speedSlider = h('input', { type: 'range', min: '0', max: '12', step: '1', value: '6', 'aria-label': 'Speed' }) as HTMLInputElement;
    const regrowSlider = h('input', { type: 'range', min: '0', max: '20', step: '1', value: '8', 'aria-label': 'Grass regrowth' }) as HTMLInputElement;
    const controls = h('div.stack', null,
      h('div.row', null, h('span.muted.small', { style: { minWidth: '56px' } }, 'Speed'), speedSlider),
      tier === 2 ? h('div.row', null, h('span.muted.small', { style: { minWidth: '56px' } }, 'Grass'), regrowSlider) : null,
      h('div.grid2', null,
        h('button.btn.small', { onclick: () => { for (let i = 0; i < 10; i++) m.R.push(rab(ctx.rng(), ctx.rng() * WH, ctx.rng)); } }, '+10 rabbits'),
        h('button.btn.small', { onclick: () => { m.rescue = true; for (let i = 0; i < 3 && m.F.length < FMAX; i++) m.F.push(fox(ctx.rng(), ctx.rng() * WH, ctx.rng)); foxesAddedAt = m.tick; } }, '+3 foxes'),
        h('button.btn.small', { onclick: () => { m.F = []; m.rescue = false; removedAt = m.tick; ctx.say('No foxes. Watch what the rabbits do.'); } }, 'Remove foxes'),
        h('button.btn.small.ghost', { onclick: () => { reset(m, ctx.rng); ctx.say(''); } }, 'Start over'),
      ),
    );
    const choices = h('div.grid2');
    const clockBtn = h('button.btn.primary.block', { onclick: () => { clock = 0; m.rescue = false; clockBtn.disabled = true; ctx.say('Clock running. Keep everyone alive for 2000 ticks.'); } }, 'Start the clock') as HTMLButtonElement;
    host.append(counts, stage, graphHost, controls, choices, clockBtn);
    clockBtn.style.display = 'none';

    const S = makeSurface(stage, { aspect: WH, draw: false });
    const G = makeSurface(graphHost, { height: 110, draw: false });
    speedSlider.addEventListener('input', () => { speed = Number(speedSlider.value); });
    regrowSlider.addEventListener('input', () => { m.regrow = Number(regrowSlider.value) * 0.0005; });
    m.regrow = 8 * 0.0005;
    const unTap = attachPointer(S.canvas, { tap(p) { const x = p.x / S.w, y = p.y / S.w; for (let i = 0; i < 3; i++) m.R.push(rab(Math.max(0, Math.min(1, x + (ctx.rng() - 0.5) * 0.05)), Math.max(0, Math.min(WH, y + (ctx.rng() - 0.5) * 0.05)), ctx.rng)); } });

    // step bookkeeping
    let foxesAddedAt = -1, removedAt = -1, baseR = -1, stepTick = 0;
    let crashPhase = 0;   // tier 1 step 3: 0 waiting for >=80, 1 waiting for <=25, 2 waiting for >=70
    let clock = -1;
    let zeroSince = -1;
    let choice: Choice | null = null;
    let choiceShown = false;

    const T1: { lesson: string; task: string }[] = [
      { lesson: 'Foxes eat rabbits. When there are more foxes, more rabbits get caught, so the rabbit count falls.', task: 'Tap +3 foxes and watch the rabbit number drop.' },
      { lesson: 'Rabbits breed fast, but the meadow can only feed so many. With no foxes, nothing keeps their number down.', task: 'Tap Remove foxes, watch for a while, then answer.' },
      { lesson: 'Many foxes eat most of the rabbits. Then the foxes go hungry, their number falls, and the rabbits come back. That rise and fall is a cycle.', task: 'Make the rabbits crash (below 25) and then recover (above 70).' },
    ];
    const T2: { lesson: string; task: string }[] = [
      { lesson: 'Grass makes its own food from sunlight, so it is called a producer. Rabbits eat grass, foxes eat rabbits. Energy moves up the chain.', task: 'Slide Grass regrowth all the way to zero, watch, then answer.' },
      { lesson: 'A living web lasts only when every link has enough to eat. Too many foxes empty the meadow; too little grass starves the rabbits.', task: 'Find a Grass setting and a fox number where grass, rabbits and foxes all last 2000 ticks. Tap Start the clock when ready.' },
      { lesson: 'Every food web starts with producers. Without them nothing else can eat.', task: 'Which one is the producer?' },
    ];
    const steps = tier === 1 ? T1 : T2;

    function showStep() {
      clear(choices); choice = null; choiceShown = false;
      setLesson(steps[si].lesson, steps[si].task);
      stepTick = m.tick; baseR = -1; crashPhase = 0; clock = -1; zeroSince = -1; removedAt = -1;
      clockBtn.style.display = tier === 2 && si === 1 ? '' : 'none'; clockBtn.disabled = false;
      if (tier === 2 && si === 2) showChoice({ prompt: 'Which one is the producer?', options: ['Grass', 'Rabbit', 'Fox', 'Bush'], answer: 0, why: 'Grass makes its own food from sunlight. Everything else in the meadow eats something.' });
    }

    function showChoice(c: Choice) {
      choice = c; choiceShown = true; clear(choices);
      c.options.forEach((o, i) => choices.append(h('button.choice', { style: { fontSize: '16px' }, onclick: () => {
        if (i === c.answer) { report(true); ctx.say(c.why); next(); }
        else { report(false); ctx.say('Not that one. Watch the numbers a little longer, then try again.'); }
      } }, o)));
    }

    function next() {
      si++;
      clear(choices); choice = null;
      if (si >= steps.length) { finish(); return; }
      setTimeout(showStep, 1500);
    }

    function watch() {
      if (choice && choiceShown) return;
      if (tier === 1) {
        if (si === 0) {
          if (baseR < 0 && m.R.length >= 40 && m.F.length >= 3 && foxesAddedAt >= stepTick) baseR = m.R.length;
          if (baseR > 0 && m.R.length <= baseR * 0.6) { report(true); ctx.say(`The rabbits fell from ${baseR} to ${m.R.length}. The foxes are eating well.`); next(); }
        } else if (si === 1) {
          if (removedAt >= stepTick && m.tick - removedAt > 700 && !choiceShown) showChoice({ prompt: 'What happens to the rabbits with no foxes?', options: ['They grow until the meadow is full', 'They disappear', 'Nothing changes', 'They turn into foxes'], answer: 0, why: 'With nothing hunting them, rabbits multiply until the meadow runs out of room. That limit is called the carrying capacity.' });
        } else if (si === 2) {
          if (crashPhase === 0 && m.R.length >= 80) crashPhase = 1;
          else if (crashPhase === 1 && m.R.length <= 25) { crashPhase = 2; ctx.say('Crash! Now wait for them to come back.'); }
          else if (crashPhase === 2 && m.R.length >= 70) { report(true); ctx.say('And they recovered. That is one full cycle.'); next(); }
          if (m.tick - stepTick > 3000 && crashPhase === 0 && m.F.length < 3) ctx.say('Hint: add foxes to crash the rabbits.');
        }
      } else {
        if (si === 0) {
          if (m.regrow === 0) { if (zeroSince < 0) zeroSince = m.tick; if (m.tick - zeroSince > 500 && !choiceShown) showChoice({ prompt: 'With no grass growing back, what happens?', options: ['Rabbits starve, then the foxes starve', 'Rabbits grow faster', 'The foxes eat grass instead', 'Nothing changes'], answer: 0, why: 'No grass means no food for rabbits. When the rabbits are gone the foxes have nothing to eat either. Everything depends on the producer.' }); }
          else zeroSince = -1;
        } else if (si === 1 && clock >= 0) {
          clock++;
          const g = grassPct(m);
          if (m.R.length < 2 || m.F.length < 1 || g < 5) {
            const who = m.R.length < 2 ? 'The rabbits died out' : m.F.length < 1 ? 'The foxes died out' : 'The grass is gone';
            report(false);
            ctx.say(`${who} after ${clock} ticks. ${m.R.length < 2 ? 'Try more grass or fewer foxes.' : m.F.length < 1 ? 'Try more rabbits so the foxes can eat.' : 'Turn grass regrowth up or add fewer rabbits.'}`);
            clock = -1; clockBtn.disabled = false; m.rescue = true;
          } else if (clock >= 2000) { report(true); ctx.say('2000 ticks and everyone is still here. A balanced web!'); clock = -1; next(); }
          else if (clock % 500 === 0) task.textContent = `Step 2 of 3: ${clock} of 2000 ticks, all alive.`;
        }
      }
    }

    function update() {
      for (let i = 0; i < speed; i++) step(m, ctx.rng);
      if (m.events.length) { ctx.say(m.events.shift()!); m.events.length = 0; }
      watch();
    }

    function draw() {
      const c = S.ctx, W = S.w, Hh = S.h;
      c.fillStyle = '#C0DD97'; c.fillRect(0, 0, W, Hh);
      if (m.grass) {
        const rows = Math.round(GRASS_N * WH), cw = W / GRASS_N, ch = Hh / rows;
        for (let i = 0; i < m.g.length; i++) { const g = m.g[i]; c.fillStyle = `rgba(99,153,34,${(g * 0.55).toFixed(2)})`; c.fillRect((i % GRASS_N) * cw, Math.floor(i / GRASS_N) * ch, cw + 0.5, ch + 0.5); if (g < 0.2) { c.fillStyle = 'rgba(180,140,90,.5)'; c.fillRect((i % GRASS_N) * cw, Math.floor(i / GRASS_N) * ch, cw + 0.5, ch + 0.5); } }
      }
      for (const [bx, by] of BUSHES) { c.fillStyle = '#639922'; c.beginPath(); c.arc(bx * W, by * W, BR * W, 0, 7); c.fill(); }
      c.lineWidth = 1;
      for (const r of m.R) { const x = r.x * W, y = r.y * W, hid = hidden(r); c.globalAlpha = hid ? 0.45 : 1; c.strokeStyle = '#444441'; c.fillStyle = '#ffffff'; c.beginPath(); c.ellipse(x - 1.6, y - 4.5, 1.1, 2.8, -0.2, 0, 7); c.fill(); c.stroke(); c.beginPath(); c.ellipse(x + 1.6, y - 4.5, 1.1, 2.8, 0.2, 0, 7); c.fill(); c.stroke(); c.beginPath(); c.arc(x, y, 3.6, 0, 7); c.fill(); c.stroke(); }
      c.globalAlpha = 1;
      for (const f of m.F) { const x = f.x * W, y = f.y * W, co = Math.cos(f.a), s = Math.sin(f.a); c.fillStyle = '#D85A30'; c.strokeStyle = '#712B13'; c.beginPath(); c.moveTo(x + co * 9, y + s * 9); c.lineTo(x - co * 6 - s * 5.5, y - s * 6 + co * 5.5); c.lineTo(x - co * 6 + s * 5.5, y - s * 6 - co * 5.5); c.closePath(); c.fill(); c.stroke(); c.fillStyle = '#fff'; c.beginPath(); c.arc(x - co * 8, y - s * 8, 2.2, 0, 7); c.fill(); }
      // graph
      const g = G.ctx, GW = G.w, GH = G.h;
      g.clearRect(0, 0, GW, GH);
      const L = 30, T = 8, B = GH - 18, gw = GW - L - 4, top = 150;
      g.font = '11px sans-serif'; g.fillStyle = '#6a6a72'; g.textAlign = 'right'; g.textBaseline = 'middle'; g.lineWidth = 0.5; g.strokeStyle = '#d8d2c2';
      for (const v of [0, 50, 100, 150]) { const y = B - (B - T) * v / top; g.beginPath(); g.moveTo(L, y); g.lineTo(GW - 4, y); g.stroke(); g.fillText(String(v), L - 6, y); }
      g.textAlign = 'left'; g.textBaseline = 'alphabetic'; g.fillText('Earlier', L, GH - 3); g.textAlign = 'right'; g.fillText('Now', GW - 4, GH - 3);
      if (m.hist.length >= 2) {
        const series: [number, string, (x: [number, number, number]) => number][] = [[0, '#378ADD', x => x[0]], [1, '#D85A30', x => x[1]]];
        if (m.grass) series.push([2, '#5fbf6a', x => x[2] * 1.5]);
        for (const [, col, f] of series) { g.strokeStyle = col; g.lineWidth = 2; g.lineJoin = 'round'; g.beginPath(); for (let i = 0; i < m.hist.length; i++) { const x = L + gw * (i + 420 - m.hist.length) / 419, y = B - (B - T) * Math.min(f(m.hist[i]), top) / top; i ? g.lineTo(x, y) : g.moveTo(x, y); } g.stroke(); }
      }
      (counts.children[0].lastElementChild as HTMLElement).textContent = String(m.R.length);
      (counts.children[1].lastElementChild as HTMLElement).textContent = String(m.F.length);
      (counts.children[2].lastElementChild as HTMLElement).textContent = `${grassPct(m)}%`;
    }

    const loop = makeLoop(() => update(), draw, 1 / 60);
    loop.start();
    showStep();
    return { destroy() { loop.stop(); unTap(); S.destroy(); G.destroy(); clear(host); } };
  }

  // ---------------- web diagram (tier 3) ----------------
  function mountDiagram() {
    const stage = h('div');
    const info = h('p.center.muted.small', null, 'Tap an animal, then tap what it eats. Tap the same pair again to remove the arrow.');
    const checkBtn = h('button.btn.primary.block', { onclick: check }, 'Check my web');
    const choices = h('div.grid2');
    host.append(stage, info, checkBtn, choices);
    const S = makeSurface(stage, { aspect: 0.95, draw: false, onResize: draw });
    const arrows = new Set<string>();   // "eater>food"
    let sel: Sp | null = null;

    const D = [
      { lesson: 'A food web shows who eats whom. Each arrow points from an eater to its food. Grass is eaten by rabbits and mice; foxes and hawks hunt rabbits and mice; owls hunt mice.', task: 'Draw every "eats" arrow, then tap Check.' },
      { lesson: 'Take one hunter out of a web and its prey get a break first. Then the animals that eat those prey get more food.', task: 'Remove the hawk. Which population grows first?' },
      { lesson: 'An animal that eats only one thing is in trouble when that thing disappears.', task: 'If every mouse vanished, which animal would be hurt most?' },
    ];

    function nodeAt(x: number, y: number): Sp | null {
      const r = Math.max(26, S.w * 0.09);
      for (const sp of SPECIES) { const [nx, ny] = NODE_POS[sp]; if (Math.hypot(x - nx * S.w, y - ny * S.h) < r) return sp; }
      return null;
    }
    const unTap = attachPointer(S.canvas, { tap(p) {
      if (si !== 0) return;
      const n = nodeAt(p.x, p.y);
      if (!n) { sel = null; draw(); return; }
      if (!sel) { sel = n; ctx.say(`${cap(n)} eats...`); }
      else if (sel === n) { sel = null; ctx.say(''); }
      else { const key = `${sel}>${n}`; if (arrows.has(key)) arrows.delete(key); else arrows.add(key); ctx.sound('pop'); sel = null; ctx.say(''); }
      draw();
    } });

    function check() {
      const wrong: string[] = []; let missing = 0;
      for (const k of arrows) { const [e, f] = k.split('>') as [Sp, Sp]; if (!EATS[e].includes(f)) wrong.push(`${cap(e)} does not eat ${f}.`); }
      for (const e of SPECIES) for (const f of EATS[e]) if (!arrows.has(`${e}>${f}`)) missing++;
      if (!wrong.length && missing === 0) { report(true); ctx.charm('charm.web'); ctx.say('That is the whole web. Every arrow is right.'); checkBtn.style.display = 'none'; next(); return; }
      report(false);
      ctx.say(wrong.length ? wrong[0] + (missing ? ` And ${missing} arrow${missing > 1 ? 's are' : ' is'} still missing.` : '') : `${missing} arrow${missing > 1 ? 's are' : ' is'} missing. Who else eats mice?`);
    }

    function showChoice(c: Choice) {
      clear(choices);
      c.options.forEach((o, i) => choices.append(h('button.choice', { style: { fontSize: '16px' }, onclick: () => {
        if (i === c.answer) { report(true); ctx.say(c.why); next(); }
        else { report(false); ctx.say('Follow the arrows in the web, then try again.'); }
      } }, o)));
    }

    function next() {
      si++;
      clear(choices);
      if (si >= D.length) { finish(); return; }
      setTimeout(showStep, 1500);
    }

    function showStep() {
      setLesson(D[si].lesson, D[si].task);
      if (si === 1) showChoice({ prompt: D[1].task, options: ['Rabbits and mice', 'Foxes', 'Grass', 'Owls'], answer: 0, why: 'Hawks hunted rabbits and mice. With one hunter gone, both prey grow first. Later the foxes and owls find more food too.' });
      if (si === 2) showChoice({ prompt: D[2].task, options: ['Owl', 'Rabbit', 'Grass', 'Hawk'], answer: 0, why: 'In this web the owl eats only mice. Foxes and hawks can switch to rabbits; the owl cannot.' });
      draw();
    }

    function draw() {
      const c = S.ctx, W = S.w, Hh = S.h;
      c.fillStyle = '#eef6e6'; c.fillRect(0, 0, W, Hh);
      const r = Math.max(24, W * 0.085);
      // arrows
      for (const k of arrows) {
        const [e, f] = k.split('>') as [Sp, Sp];
        const [ax, ay] = NODE_POS[e], [bx, by] = NODE_POS[f];
        const x1 = ax * W, y1 = ay * Hh, x2 = bx * W, y2 = by * Hh;
        const d = Math.hypot(x2 - x1, y2 - y1), ux = (x2 - x1) / d, uy = (y2 - y1) / d;
        const sx = x1 + ux * r, sy = y1 + uy * r, ex = x2 - ux * (r + 4), ey = y2 - uy * (r + 4);
        const ok = EATS[e].includes(f);
        c.strokeStyle = si > 0 ? (ok ? '#2a2a2e' : '#e2574c') : '#2a2a2e'; c.lineWidth = 3;
        c.beginPath(); c.moveTo(sx, sy); c.lineTo(ex, ey); c.stroke();
        c.fillStyle = c.strokeStyle; c.beginPath(); c.moveTo(ex + ux * 6, ey + uy * 6); c.lineTo(ex - uy * 6, ey + ux * 6); c.lineTo(ex + uy * 6, ey - ux * 6); c.closePath(); c.fill();
      }
      for (const sp of SPECIES) {
        const [nx, ny] = NODE_POS[sp];
        const x = nx * W, y = ny * Hh;
        c.fillStyle = NODE_COLOR[sp]; c.beginPath(); c.arc(x, y, r, 0, 7); c.fill();
        c.lineWidth = sel === sp ? 4 : 2; c.strokeStyle = sel === sp ? '#3bb3a9' : '#2a2a2e'; c.stroke();
        c.fillStyle = sp === 'rabbit' || sp === 'grass' || sp === 'mouse' ? '#2a2a2e' : '#fff';
        c.font = `800 ${Math.round(r * 0.48)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
        c.fillText(cap(sp), x, y);
      }
      c.fillStyle = '#6a6a72'; c.font = '700 12px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'alphabetic';
      c.fillText('arrow = eats', 8, Hh - 8);
      roundRect(c, 0, 0, 0, 0, 0);
    }

    showStep();
    return { destroy() { unTap(); S.destroy(); clear(host); } };
  }
}

function cap(s: string): string { return s[0].toUpperCase() + s.slice(1); }

const mod: GameModule = {
  id: 'science.food-web',
  title: 'Food Web',
  blurb: 'Rabbits, foxes and grass in a meadow. Who eats whom, and what happens when one disappears?',
  category: 'science',
  skills: ['biology'],
  levelRange: [1, 10],
  unlock: { stars: 13 },
  mount,
};
export default mod;
